import { useEffect, useRef } from 'react';
import { trackScrollDepth } from '../utils/analytics';

/**
 * Custom Hook: useScrollTracking
 * 
 * Tracks user scroll depth on a page for Google Analytics.
 * Fires events at 25%, 50%, 75%, and 100% scroll depth.
 * 
 * @param {string} pageName - Name of the current page for analytics
 * 
 * @example
 * function HomePage() {
 *   useScrollTracking('home');
 *   return <div>...</div>;
 * }
 */
const useScrollTracking = (pageName) => {
  const scrollDepthsTracked = useRef({
    25: false,
    50: false,
    75: false,
    100: false
  });

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Calculate scroll percentage
      const scrollableHeight = documentHeight - windowHeight;
      const scrollPercentage = (scrollTop / scrollableHeight) * 100;

      // Track milestones
      [25, 50, 75, 100].forEach(milestone => {
        if (scrollPercentage >= milestone && !scrollDepthsTracked.current[milestone]) {
          scrollDepthsTracked.current[milestone] = true;
          trackScrollDepth(milestone, pageName);
        }
      });
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pageName]);

  // Reset tracking when page changes
  useEffect(() => {
    scrollDepthsTracked.current = {
      25: false,
      50: false,
      75: false,
      100: false
    };
  }, [pageName]);
};

export default useScrollTracking;
